<template>
    <v-dialog :value="value" max-width="560" content-class="dfd-shell" overlay-color="#0d2c6d" overlay-opacity="0.42"
        @input="$emit('input', $event)" @click:outside="$emit('dismiss')">
        <div v-if="draft" class="dfd" role="dialog" aria-labelledby="dfd-title">

            <!-- כותרת -->
            <div class="dfd__head">
                <span class="dfd__badge" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"
                        stroke-linejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M12 12v4l2.5 1.5" />
                    </svg>
                </span>
                <div class="dfd__head-text">
                    <h2 id="dfd-title" class="dfd__title">יש טיוטה שמורה</h2>
                    <p class="dfd__sub">{{ typeLabel }}<span v-if="savedAgo">, נשמרה {{ savedAgo }}</span></p>
                </div>
                <button type="button" class="dfd__close" aria-label="סגירה" @click="$emit('dismiss')">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                        stroke-linecap="round">
                        <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <!-- עד לאיזה שלב הגיע הניסיון הקודם -->
            <div class="dfd__trace" :aria-label="`הניסיון הקודם הגיע לשלב ${reachedStep} מתוך 4`">
                <div v-for="(name, i) in stepNames" :key="i" class="dfd__seg"
                    :class="{ 'is-done': i + 1 < reachedStep, 'is-here': i + 1 === reachedStep }">
                    <span class="dfd__seg-bar"></span>
                    <span class="dfd__seg-label">{{ name }}</span>
                </div>
            </div>

            <div class="dfd__body">
                <div v-if="draft.fail_reason" class="dfd__fail">
                    <span class="dfd__fail-label">הניסיון הקודם נעצר</span>
                    <span class="dfd__fail-text">{{ draft.fail_reason }}</span>
                </div>

                <div class="dfd__facts">
                    <div v-if="draft.customer_name" class="dfd__fact">
                        <span class="dfd__fact-key">לקוח</span>
                        <span class="dfd__fact-val">{{ draft.customer_name }}</span>
                    </div>
                    <div class="dfd__fact">
                        <span class="dfd__fact-key">תעודת זהות</span>
                        <span class="dfd__fact-val dfd__num">{{ draft.card_id }}</span>
                    </div>
                    <div v-if="draft.manui_type" class="dfd__fact">
                        <span class="dfd__fact-key">סוג מנוי</span>
                        <span class="dfd__fact-val">{{ draft.manui_type }}</span>
                    </div>
                    <div v-if="packageLabel" class="dfd__fact">
                        <span class="dfd__fact-key">חבילה</span>
                        <span class="dfd__fact-val">{{ packageLabel }}</span>
                    </div>
                    <div v-if="draft.sim_number" class="dfd__fact">
                        <span class="dfd__fact-key">מספר סים</span>
                        <span class="dfd__fact-val dfd__num">{{ draft.sim_number }}</span>
                    </div>
                    <div v-if="draft.manui_number" class="dfd__fact">
                        <span class="dfd__fact-key">מספר מנוי</span>
                        <span class="dfd__fact-val dfd__num">{{ draft.manui_number }}</span>
                    </div>
                </div>

                <div class="dfd__notes">
                    <div class="dfd__note">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <rect x="2" y="5" width="20" height="14" rx="2" />
                            <path d="M2 10h20" />
                        </svg>
                        <span>פרטי התשלום אינם נשמרים ויש להזין אותם מחדש</span>
                    </div>
                    <div v-if="draft.needs_id_image" class="dfd__note">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path
                                d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3z" />
                            <circle cx="12" cy="13" r="3.2" />
                        </svg>
                        <span>יש לצרף מחדש את צילום תעודת הזהות</span>
                    </div>
                </div>
            </div>

            <div class="dfd__actions">
                <button type="button" class="dfd__btn dfd__btn--primary" @click="$emit('apply')">מילוי הפרטים</button>
                <button type="button" class="dfd__btn dfd__btn--ghost" @click="$emit('dismiss')">התחלה מחדש</button>
                <span class="dfd__spacer"></span>
                <button type="button" class="dfd__btn dfd__btn--quiet" @click="$emit('delete')">מחיקת הטיוטה</button>
            </div>

            <p v-if="daysLeft" class="dfd__foot">הטיוטה תימחק אוטומטית בעוד {{ daysLeft }}</p>
        </div>
    </v-dialog>
</template>

<script>
const TYPE_LABELS = {
    customer_and_manui: "לקוח חדש והקמת מנוי",
    manui_only: "הקמת מנוי ללקוח קיים",
    customer_only: "יצירת לקוח בלבד",
};

const COMPANY_LABELS = {
    partner: "פרטנר",
    hotMobile: "הוט מובייל",
    clickCallMobile: "קליקול מובייל",
};

export default {
    name: "DraftFoundDialog",
    props: {
        value: { type: Boolean, default: false },
        draft: { type: Object, default: null },
        planName: { type: String, default: "" },
    },
    data: () => ({
        stepNames: ["פרטים", "חבילה", "תשלום", "סים"],
    }),
    computed: {
        typeLabel() {
            return TYPE_LABELS[this.draft && this.draft.type] || "";
        },
        packageLabel() {
            const company = COMPANY_LABELS[this.draft && this.draft.operating_company] || "";
            return [this.planName, company].filter(Boolean).join(", ");
        },
        reachedStep() {
            const step = Number(this.draft && this.draft.last_step) || 1;
            return Math.min(Math.max(step, 1), 4);
        },
        savedAgo() {
            const raw = this.draft && (this.draft.updatedAt || this.draft.createdAt);
            if (!raw) return "";

            const minutes = Math.floor((Date.now() - new Date(raw).getTime()) / 60000);
            if (minutes < 2) return "לפני רגע";
            if (minutes < 60) return `לפני ${minutes} דקות`;

            const hours = Math.floor(minutes / 60);
            if (hours < 24) return hours === 1 ? "לפני שעה" : `לפני ${hours} שעות`;

            const days = Math.floor(hours / 24);
            return days === 1 ? "אתמול" : `לפני ${days} ימים`;
        },
        daysLeft() {
            if (!this.draft || !this.draft.expiresAt) return "";

            const ms = new Date(this.draft.expiresAt).getTime() - Date.now();
            if (ms <= 0) return "";

            const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
            return days === 1 ? "יום" : `${days} ימים`;
        },
    },
};
</script>

<style scoped>
/* הדיאלוג מרונדר מחוץ ל-.cm-page ולכן ה-tokens מוגדרים כאן מחדש */
.dfd {
    --c-navy: #0d2c6d;
    --c-navy-deep: #081f54;
    --c-gold: #fdb721;
    --c-surface: #ffffff;
    --c-border: #e3e8ef;
    --c-border-soft: #eef2f7;
    --c-text: #0f172a;
    --c-text-soft: #475569;
    --c-text-mute: #94a3b8;
    --c-danger: #b40d0d;
    --ease: cubic-bezier(0.22, 1, 0.36, 1);

    direction: rtl;
    background: var(--c-surface);
    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, 'Segoe UI', sans-serif;
    color: var(--c-text);
    padding: 22px 24px 18px;
}

/* ───────────── כותרת ───────────── */
.dfd__head {
    display: flex;
    align-items: flex-start;
    gap: 13px;
}

.dfd__badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    border-radius: 13px;
    background: rgba(253, 183, 33, 0.16);
    color: #a56b00;
}

.dfd__badge svg {
    width: 22px;
    height: 22px;
}

.dfd__head-text {
    flex: 1;
    min-width: 0;
    padding-top: 1px;
}

.dfd__title {
    margin: 0;
    font-size: 1.16rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: 1.3;
}

.dfd__sub {
    margin: 2px 0 0;
    font-size: 0.87rem;
    color: var(--c-text-mute);
    line-height: 1.4;
}

.dfd__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    margin: -4px -6px 0 0;
    border: none;
    border-radius: 9px;
    background: transparent;
    color: var(--c-text-mute);
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.18s var(--ease), color 0.18s var(--ease);
}

.dfd__close:hover {
    background: var(--c-border-soft);
    color: var(--c-text-soft);
}

.dfd__close svg {
    width: 16px;
    height: 16px;
}

/* ───────────── עד לאיזה שלב הגיעו ───────────── */
.dfd__trace {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
    margin: 18px 0 4px;
}

.dfd__seg-bar {
    display: block;
    height: 4px;
    border-radius: 3px;
    background: var(--c-border);
    transition: background 0.25s var(--ease);
}

.dfd__seg-label {
    display: block;
    margin-top: 6px;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--c-text-mute);
}

.dfd__seg.is-done .dfd__seg-bar {
    background: var(--c-navy);
    opacity: 0.35;
}

.dfd__seg.is-done .dfd__seg-label {
    color: var(--c-text-soft);
}

.dfd__seg.is-here .dfd__seg-bar {
    background: var(--c-gold);
}

.dfd__seg.is-here .dfd__seg-label {
    color: var(--c-navy);
    font-weight: 800;
}

/* ───────────── גוף ───────────── */
.dfd__body {
    margin-top: 18px;
}

.dfd__fail {
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 9px 13px;
    margin-bottom: 14px;
    border-radius: 10px;
    background: #fef4f4;
    border-inline-start: 3px solid var(--c-danger);
}

.dfd__fail-label {
    font-size: 0.73rem;
    font-weight: 700;
    color: #9a1616;
}

.dfd__fail-text {
    font-size: 0.89rem;
    color: #7f1d1d;
    line-height: 1.5;
}

.dfd__facts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1px;
    background: var(--c-border-soft);
    border: 1px solid var(--c-border-soft);
    border-radius: 12px;
    overflow: hidden;
}

.dfd__fact {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 11px 14px;
    background: var(--c-surface);
}

.dfd__fact-key {
    font-size: 0.73rem;
    font-weight: 600;
    color: var(--c-text-mute);
}

.dfd__fact-val {
    font-size: 0.94rem;
    font-weight: 700;
    color: var(--c-text);
    overflow-wrap: anywhere;
}

.dfd__num {
    font-feature-settings: "tnum" 1;
    letter-spacing: 0.01em;
}

.dfd__notes {
    margin-top: 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.dfd__note {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
    color: var(--c-text-soft);
    line-height: 1.45;
}

.dfd__note svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: var(--c-text-mute);
}

/* ───────────── פעולות ───────────── */
.dfd__actions {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-top: 22px;
    padding-top: 16px;
    border-top: 1px solid var(--c-border-soft);
}

.dfd__spacer {
    flex: 1;
}

.dfd__btn {
    font-family: inherit;
    font-size: 0.9rem;
    font-weight: 700;
    border-radius: 10px;
    border: 1px solid transparent;
    cursor: pointer;
    padding: 10px 20px;
    line-height: 1.2;
    transition: background 0.2s var(--ease), border-color 0.2s var(--ease),
        color 0.2s var(--ease), box-shadow 0.2s var(--ease), transform 0.12s var(--ease);
}

.dfd__btn:focus-visible {
    outline: 2px solid var(--c-navy);
    outline-offset: 2px;
}

.dfd__btn:active {
    transform: translateY(1px);
}

.dfd__btn--primary {
    background: var(--c-navy);
    color: #fff;
    box-shadow: 0 6px 16px -6px rgba(13, 44, 109, 0.5);
}

.dfd__btn--primary:hover {
    background: var(--c-navy-deep);
}

.dfd__btn--ghost {
    background: var(--c-surface);
    border-color: var(--c-border);
    color: var(--c-text-soft);
}

.dfd__btn--ghost:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: var(--c-text);
}

.dfd__btn--quiet {
    background: transparent;
    color: var(--c-text-mute);
    padding: 10px 8px;
    font-size: 0.84rem;
    font-weight: 600;
}

.dfd__btn--quiet:hover {
    color: var(--c-danger);
}

.dfd__foot {
    margin: 12px 0 0;
    font-size: 0.76rem;
    color: var(--c-text-mute);
    text-align: center;
}

/* ───────────── מסכים צרים ───────────── */
@media (max-width: 520px) {
    .dfd {
        padding: 18px 16px 14px;
    }

    .dfd__facts {
        grid-template-columns: 1fr;
    }

    .dfd__seg-label {
        font-size: 0.66rem;
    }

    .dfd__actions {
        flex-wrap: wrap;
    }

    .dfd__btn--primary,
    .dfd__btn--ghost {
        flex: 1 1 auto;
    }

    .dfd__spacer {
        display: none;
    }

    .dfd__btn--quiet {
        width: 100%;
        margin-top: 2px;
    }
}

@media (prefers-reduced-motion: reduce) {

    .dfd__btn,
    .dfd__close,
    .dfd__seg-bar {
        transition: none;
    }
}
</style>

<style>
/* ה-content-class יושב מחוץ ל-scope של הקומפוננטה */
.dfd-shell {
    border-radius: 18px !important;
    box-shadow: 0 24px 60px -16px rgba(8, 31, 84, 0.35), 0 10px 22px -12px rgba(8, 31, 84, 0.22) !important;
    overflow: hidden;
}
</style>